import {
  automationsBootstrapSchema,
  type AutomationConnectionState,
  type AutomationNotification,
  type AutomationsBootstrap,
  type WorkflowDefinition,
  type WorkflowExecution,
} from "@crewfsm-extension/automations-contracts";

import type { AutomationsAppSession } from "./app-session";

export class RuntimeWorkflowStore {
  private snapshot: AutomationsBootstrap | null = null;

  constructor(private readonly session: AutomationsAppSession) {}

  async hydrate(): Promise<void> {
    this.snapshot = automationsBootstrapSchema.parse(
      await this.request("/v1/bootstrap"),
    );
  }

  list(): WorkflowDefinition[] {
    return this.snapshot?.workflows ?? [];
  }

  find(id: string): WorkflowDefinition | null {
    return this.list().find((workflow) => workflow.id === id) ?? null;
  }

  listExecutions(): WorkflowExecution[] {
    return this.snapshot?.executions ?? [];
  }

  listNotifications(): AutomationNotification[] {
    return this.snapshot?.notifications ?? [];
  }

  connection(): AutomationConnectionState | null {
    return this.snapshot?.connection ?? null;
  }

  async save(workflow: WorkflowDefinition): Promise<void> {
    await this.request(`/v1/workflows/${encodeURIComponent(workflow.id)}`, {
      body: JSON.stringify(workflow),
      method: "PUT",
    });
    await this.hydrate();
  }

  async remove(id: string): Promise<void> {
    await this.request(`/v1/workflows/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    await this.hydrate();
  }

  async appendExecution(execution: WorkflowExecution): Promise<void> {
    await this.request("/v1/test-runs", {
      body: JSON.stringify(execution),
      method: "POST",
    });
    await this.hydrate();
  }

  async configureSigningSecret(secret: string): Promise<void> {
    await this.request("/v1/settings/signing-secret", {
      body: JSON.stringify({ secret }),
      method: "PUT",
    });
    await this.hydrate();
  }

  async retryExecution(executionId: string): Promise<void> {
    await this.request(
      `/v1/executions/${encodeURIComponent(executionId)}/retry`,
      { method: "POST" },
    );
    await this.hydrate();
  }

  private async request(
    path: string,
    init: RequestInit = {},
    refreshed = false,
  ): Promise<unknown> {
    const response = await fetch(`/runtime${path}`, {
      ...init,
      headers: {
        authorization: `Bearer ${this.session.bridge.getSessionToken()}`,
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...init.headers,
      },
    });
    if (response.status === 401 && !refreshed) {
      await this.session.bridge.refreshContext();
      return this.request(path, init, true);
    }
    const body = response.status === 204 ? null : await response.json();
    if (!response.ok) {
      throw new AutomationsRuntimeError(
        typeof body === "object" && body !== null && "error" in body
          ? String(body.error)
          : `Automations runtime returned HTTP ${response.status}.`,
      );
    }
    return body;
  }
}

export class AutomationsRuntimeError extends Error {}
